// Xem nhanh thông tin tài liệu (drawer) + gắn nhãn cá nhân
import { useState } from 'react';
import { Button, Drawer, Popover, Select, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Tag as TagIcon } from 'lucide-react';
import { blockName, deptName, issuerName, type Doc } from '../data';
import { categoryPath, fmtSize } from '../logic';
import useDocsStore from '../store';
import { FileTypeIcon, StatePill, docLink, fmtDateTime, fmtEffective, personName, usePeople } from '../utils';

export const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-3 gap-2 border-b border-slate-100 py-2 text-sm last:border-0">
    <span className="text-slate-400">{label}</span>
    <span className="col-span-2 min-w-0 break-words text-slate-800">{children}</span>
  </div>
);

export function QuickInfoDrawer({ doc, onClose }: { doc: Doc | null; onClose: () => void }) {
  const navigate = useNavigate();
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const people = usePeople();
  return (
    <Drawer
      open={!!doc} onClose={onClose} width={440} title="Thông tin tài liệu" destroyOnHidden
      extra={doc && <Button type="primary" icon={<ExternalLink size={14} />} onClick={() => navigate(docLink(doc.id))}>Mở chi tiết</Button>}
    >
      {doc && (
        <>
          <div className="mb-1 text-xs text-slate-400">{doc.code}</div>
          <div className="mb-2 text-base font-semibold text-slate-800">{doc.title}</div>
          <div className="mb-4"><StatePill doc={doc} /></div>
          <InfoRow label="Loại">{types.find(t => t.id === doc.typeId)?.name || '—'}</InfoRow>
          <InfoRow label="Danh mục">{categoryPath(categories, doc.categoryId)}</InfoRow>
          <InfoRow label="Hiệu lực">{fmtEffective(doc)}</InfoRow>
          <InfoRow label="Đơn vị ban hành">{deptName(doc.unitId)}</InfoRow>
          <InfoRow label="Khối/nghiệp vụ">{blockName(doc.blockId)}</InfoRow>
          <InfoRow label="Chủ thể ban hành">{issuerName(doc.issuerId)}</InfoRow>
          <InfoRow label="Người ký">{personName(people, doc.signerId)}</InfoRow>
          <InfoRow label="Người tạo">{doc.createdByName} · {fmtDateTime(doc.createdAt)}</InfoRow>
          {doc.summary && <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{doc.summary}</div>}
          <div className="mb-2 mt-4 text-sm font-semibold text-slate-700">File đính kèm ({doc.files.length})</div>
          {doc.files.map(f => (
            <div key={f.id} className="flex items-center gap-2 py-1 text-sm">
              <FileTypeIcon ext={f.ext} />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="text-xs text-slate-400">{fmtSize(f.size)}</span>
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
}

const NO_LABELS: string[] = [];

/** Gắn nhãn cá nhân cho 1 tài liệu (chỉ người dùng hiện tại thấy) */
export function LabelPicker({ docId }: { docId: string }) {
  const labels = useDocsStore(s => s.myLabels[docId]) || NO_LABELS;
  const labelList = useDocsStore(s => s.labelList);
  const setLabels = useDocsStore(s => s.setLabels);
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open} onOpenChange={setOpen} trigger="click" title="Gắn nhãn cá nhân"
      content={
        <div className="w-64">
          <Select
            mode="tags" className="w-full" value={labels} placeholder="Chọn hoặc gõ nhãn mới rồi Enter"
            options={labelList.map(l => ({ value: l, label: l }))}
            onChange={v => setLabels(docId, (v as string[]).map(x => x.trim()).filter(Boolean).slice(0, 5))}
            maxTagTextLength={20}
          />
          <div className="mt-1 text-xs text-slate-400">Tối đa 5 nhãn / tài liệu</div>
        </div>
      }
    >
      <button title="Gắn nhãn" className="text-slate-400 hover:text-[#3e91ab]" onClick={e => e.stopPropagation()}><TagIcon size={15} /></button>
    </Popover>
  );
}

export const LabelTags = ({ docId }: { docId: string }) => {
  const labels = useDocsStore(s => s.myLabels[docId]);
  if (!labels?.length) return null;
  return <>{labels.map(l => <Tag key={l} color="cyan" className="!mr-1 !text-[11px]">{l}</Tag>)}</>;
};
